<template>
  <div id="project_rate_setting" class="project-rate-page">
    <CCard>
      <CCardHeader>
        <h2>
          <span>プロジェクト単価設定</span>
          <span class="card-header-actions">
            <router-link to="/admin/total/list_project">
              <button type="button" class="btn btn-outline-primary">
                プロジェクト集計へ
              </button>
            </router-link>
          </span>
        </h2>
      </CCardHeader>

      <CCardBody>
        <div class="project-rate-search">
          <div>
            <label for="project-rate-year">対象年月</label>
            <div class="project-rate-search__period">
              <select
                id="project-rate-year"
                class="custom-select"
                v-model="year"
                :disabled="loading || saving"
              >
                <option
                  v-for="option in yearOption"
                  :key="option.id"
                  :value="option.id"
                >
                  {{ option.name }}
                </option>
              </select>
              <select
                id="project-rate-month"
                class="custom-select"
                v-model="month"
                :disabled="loading || saving"
              >
                <option
                  v-for="option in monthOption"
                  :key="option.id"
                  :value="option.id"
                >
                  {{ option.name }}
                </option>
              </select>
              <span>業務分</span>
            </div>
          </div>

          <div class="project-rate-search__actions">
            <button
              type="button"
              class="btn btn-primary"
              :disabled="loading || saving"
              @click="loadRates"
            >
              表示
            </button>
          </div>
        </div>
      </CCardBody>

      <template v-if="loaded">
        <CCardBody class="project-rate-result">
          <div class="project-rate-heading">
            <div>
              <p>MONTHLY PROJECT RATE</p>
              <h3>{{ year }}年{{ month }}月 単価設定</h3>
            </div>
            <div class="project-rate-heading__actions">
              <button
                type="button"
                class="btn btn-secondary"
                :disabled="saving || items.length === 0 || previousConfiguredCount === 0"
                @click="copyPreviousAll"
              >
                直近単価を一括反映
              </button>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="saving || items.length === 0 || hasValidationError"
                @click="saveRates"
              >
                <span v-if="saving" class="my-spinner" aria-hidden="true"></span>
                {{ saving ? '保存中' : '保存' }}
              </button>
            </div>
          </div>

          <div class="project-rate-guide">
            <div>
              <strong>請求基準単価</strong>
              <span>請求書の基本単価とプロジェクト集計の見込売上計算に使用します。</span>
            </div>
            <div>
              <strong>月額原価</strong>
              <span>プロジェクト集計の原価・粗利計算に使用します。</span>
            </div>
            <div>
              <strong>継続と当月除外</strong>
              <span>最新の有効単価は翌月以降も自動継続します。不要な月だけ「当月除外」を保存できます。</span>
            </div>
          </div>

          <div class="alert alert-info">
            管理者向けの全体設定です。ログイン中アカウントの全プロジェクト・全メンバーを表示します。
          </div>

          <div v-if="items.length === 0" class="preika-empty-state">
            <div>
              <strong>単価設定対象のメンバーがありません</strong>
              <p>プロジェクト所属または対象月の勤務実績を確認してください。</p>
            </div>
          </div>

          <div v-else class="table-responsive project-rate-table">
            <table class="table table-bordered">
              <thead>
                <tr>
                  <th>顧客</th>
                  <th>プロジェクト</th>
                  <th>メンバー</th>
                  <th class="text-center">基準時間</th>
                  <th class="text-right">直近請求単価</th>
                  <th class="text-right">直近原価</th>
                  <th class="text-right">請求基準単価</th>
                  <th class="text-right">月額原価</th>
                  <th class="text-center">当月対象</th>
                  <th class="text-center">状態</th>
                  <th class="text-center">操作</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="group in groupedItems">
                  <tr
                    v-for="(item, index) in group.items"
                    :key="item.projectId + '-' + item.userId"
                  >
                    <td
                      v-if="index === 0"
                      :rowspan="group.items.length"
                      class="project-rate-table__group-cell"
                    >
                      {{ group.companyName }}
                    </td>
                    <td
                      v-if="index === 0"
                      :rowspan="group.items.length"
                      class="project-rate-table__group-cell"
                    >
                      <strong>{{ group.projectName }}</strong>
                    </td>
                    <td>{{ item.userName }}</td>
                    <td class="text-center">
                      {{ item.baseTimeMin || '-' }}〜{{ item.baseTimeMax || '-' }}h
                    </td>
                    <td class="text-right project-rate-table__previous">
                      {{ formatCurrency(item.previousBillingUnitPrice) }}
                      <small v-if="item.inheritedYearMonth">
                        {{ formatYearMonth(item.inheritedYearMonth) }}設定
                      </small>
                    </td>
                    <td class="text-right project-rate-table__previous">
                      {{ formatCurrency(item.previousCostUnitPrice) }}
                    </td>
                    <td class="project-rate-table__input">
                      <CInput
                        v-model="item.billingUnitPrice"
                        type="number"
                        min="0"
                        step="1"
                        inputmode="numeric"
                        class="text-right"
                        :disabled="saving || item.excluded"
                        :is-valid="item.excluded || isValidPrice(item.billingUnitPrice) ? null : false"
                        @input="markEdited(item)"
                      />
                      <small v-if="!item.excluded && !isValidPrice(item.billingUnitPrice)" class="error">
                        0以上の整数で入力してください。
                      </small>
                    </td>
                    <td class="project-rate-table__input">
                      <CInput
                        v-model="item.costUnitPrice"
                        type="number"
                        min="0"
                        step="1"
                        inputmode="numeric"
                        class="text-right"
                        :disabled="saving || item.excluded"
                        :is-valid="item.excluded || isValidPrice(item.costUnitPrice) ? null : false"
                        @input="markEdited(item)"
                      />
                      <small v-if="!item.excluded && !isValidPrice(item.costUnitPrice)" class="error">
                        0以上の整数で入力してください。
                      </small>
                    </td>
                    <td class="text-center project-rate-table__exclusion">
                      <label class="project-rate-exclusion">
                        <input
                          v-model="item.excluded"
                          type="checkbox"
                          :disabled="saving || (!item.excluded && !item.excludable)"
                          @change="onExcludedChange(item)"
                        >
                        <span>当月除外</span>
                      </label>
                      <small v-if="!item.excludable">
                        勤務・売上実績あり
                      </small>
                    </td>
                    <td class="text-center">
                      <span
                        class="project-rate-status"
                        :class="rateStatusClass(item)"
                      >
                        {{ rateStatusLabel(item) }}
                      </span>
                    </td>
                    <td class="text-center">
                      <button
                        type="button"
                        class="btn btn-secondary btn-sm"
                        :disabled="saving || item.excluded || !hasPreviousRate(item)"
                        @click="copyPrevious(item)"
                      >
                        直近反映
                      </button>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <div v-if="items.length" class="project-rate-footer">
            <p>
              最新の有効単価は翌月以降も自動継続します。当月だけ対象外にする場合は「当月除外」を保存してください。勤務・売上実績がある行は除外できません。
            </p>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="saving || hasValidationError"
              @click="saveRates"
            >
              <span v-if="saving" class="my-spinner" aria-hidden="true"></span>
              {{ saving ? '保存中' : `${items.length}件を保存` }}
            </button>
          </div>
        </CCardBody>
      </template>
    </CCard>

    <div v-if="loading" class="loading-overlay" aria-live="polite" aria-busy="true">
      <CSpinner color="primary" style="width: 3rem; height: 3rem;" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import ConstList from './../../ConstList'

export default {
  name: 'ProjectRateSetting',

  data () {
    return {
      year: '',
      month: '',
      yearOption: [],
      monthOption: [],
      loaded: false,
      loading: false,
      saving: false,
      items: [],
      previousYearMonth: ''
    }
  },

  computed: {
    groupedItems () {
      const groups = []
      const indexByProject = {}

      this.items.forEach(item => {
        const key = String(item.projectId)
        let groupIndex = indexByProject[key]

        if (groupIndex === undefined) {
          groupIndex = groups.length
          indexByProject[key] = groupIndex
          groups.push({
            projectId: item.projectId,
            projectName: item.projectName,
            companyName: item.companyName,
            items: []
          })
        }
        groups[groupIndex].items.push(item)
      })

      return groups
    },

    hasValidationError () {
      return this.items.some(item =>
        !item.excluded && (
          !this.isValidPrice(item.billingUnitPrice) ||
          !this.isValidPrice(item.costUnitPrice)
        )
      )
    },

    previousConfiguredCount () {
      return this.items.filter(item => !item.excluded && this.hasPreviousRate(item)).length
    }
  },

  created () {
    this.yearOption = ConstList.YEAR_OPTION
    this.monthOption = ConstList.MONTH_OPTION
  },

  mounted () {
    const saved = sessionStorage.getItem('project-rate-search-params')
    if (saved) {
      const params = JSON.parse(saved)
      this.year = params.year
      this.month = params.month
      this.loadRates()
      return
    }

    const target = moment()
    this.year = target.format('YYYY')
    this.month = target.format('M')
    this.loadRates()
  },

  methods: {
    loadRates () {
      if (this.loading || this.saving) return

      this.loading = true
      this.loaded = false

      axios
        .get(process.env.VUE_APP_API_ORIGIN + '/admin/project-rate', {
          params: {
            year: this.year,
            month: this.month
          }
        })
        .then(response => {
          const data = response.data || {}
          this.previousYearMonth = data.previousYearMonth || ''
          this.items = (Array.isArray(data.items) ? data.items : []).map(row => ({
            ...row,
            billingUnitPrice: this.normalizePrice(row.billingUnitPrice),
            costUnitPrice: this.normalizePrice(row.costUnitPrice),
            previousBillingUnitPrice: this.normalizePrice(row.previousBillingUnitPrice),
            previousCostUnitPrice: this.normalizePrice(row.previousCostUnitPrice),
            inheritedYearMonth: row.inheritedYearMonth || '',
            inherited: row.inherited === true,
            excluded: row.excluded === true,
            excludable: row.excludable !== false,
            edited: false
          }))
          this.loaded = true

          sessionStorage.setItem(
            'project-rate-search-params',
            JSON.stringify({ year: this.year, month: this.month })
          )
        })
        .catch(error => {
          this.handleError(error)
        })
        .finally(() => {
          this.loading = false
        })
    },

    saveRates () {
      if (this.saving || this.items.length === 0 || this.hasValidationError) return

      this.saving = true
      const payload = {
        year: this.year,
        month: this.month,
        items: this.items.map(item => ({
          project_id: item.projectId,
          user_id: item.userId,
          billing_unit_price: Number(item.billingUnitPrice),
          cost_unit_price: Number(item.costUnitPrice),
          excluded: item.excluded === true
        }))
      }

      axios
        .post(
          process.env.VUE_APP_API_ORIGIN + '/admin/project-rate',
          payload,
          { headers: { 'Content-Type': 'application/json' } }
        )
        .then(() => {
          alert(ConstList.REGISTER_MESSAGE)
          this.saving = false
          this.loadRates()
        })
        .catch(error => {
          this.handleError(error)
        })
        .finally(() => {
          this.saving = false
        })
    },

    copyPrevious (item) {
      if (item.excluded || !this.hasPreviousRate(item)) return
      item.billingUnitPrice = this.normalizePrice(item.previousBillingUnitPrice)
      item.costUnitPrice = this.normalizePrice(item.previousCostUnitPrice)
      item.edited = true
    },

    copyPreviousAll () {
      this.items.forEach(item => {
        if (!item.excluded && this.hasPreviousRate(item)) {
          this.copyPrevious(item)
        }
      })
    },

    hasPreviousRate (item) {
      return Boolean(item.inheritedYearMonth) ||
        Number(item.previousBillingUnitPrice) > 0 ||
        Number(item.previousCostUnitPrice) > 0
    },

    onExcludedChange (item) {
      if (!item.excluded && this.hasPreviousRate(item) &&
          Number(item.billingUnitPrice) === 0 && Number(item.costUnitPrice) === 0) {
        this.copyPrevious(item)
      }
      item.edited = true
    },

    rateStatusLabel (item) {
      if (item.excluded) return '当月除外'
      if (item.configured) return '設定済'
      if (item.inherited) return '継続'
      return '未設定'
    },

    rateStatusClass (item) {
      if (item.excluded) return 'project-rate-status--excluded'
      if (item.configured) return 'project-rate-status--saved'
      if (item.inherited) return 'project-rate-status--inherited'
      return 'project-rate-status--new'
    },

    formatYearMonth (value) {
      const text = String(value || '')
      if (!/^\d{6}$/.test(text)) return ''
      return `${text.slice(0, 4)}年${Number(text.slice(4, 6))}月`
    },

    markEdited (item) {
      item.edited = true
    },

    isValidPrice (value) {
      if (value === '' || value === null || value === undefined) return false
      const number = Number(value)
      return Number.isFinite(number) && number >= 0 && Number.isInteger(number) && number <= 999999999999
    },

    normalizePrice (value) {
      const number = Number(value)
      return Number.isFinite(number) && number >= 0 ? Math.round(number) : 0
    },

    formatCurrency (value) {
      return this.normalizePrice(value).toLocaleString('ja-JP')
    },

    handleError (error) {
      axios
        .post(
          `${process.env.VUE_APP_API_ORIGIN}/error/frontError`,
          { error: String(error) },
          { headers: { 'Content-Type': 'application/json' } }
        )
        .then(() => alert(ConstList.SYSTEM_ERROR))
        .catch(() => alert(ConstList.SYSTEM_COMMUNICATION_ERROR))
    }
  }
}
</script>

<style scoped>
.project-rate-search,
.project-rate-heading,
.project-rate-footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
}

.project-rate-search__period,
.project-rate-search__actions,
.project-rate-heading__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.project-rate-search__period .custom-select {
  width: 100px;
}

.project-rate-search__period .custom-select:nth-child(2) {
  width: 78px;
}

.project-rate-result {
  border-top: 1px solid var(--preika-border);
}

.project-rate-heading {
  margin-bottom: 18px;
}

.project-rate-heading p {
  margin: 0 0 4px;
  color: var(--preika-blue-600);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .15em;
}

.project-rate-heading h3 {
  margin: 0;
  color: var(--preika-navy-900);
  font-size: 22px;
}

.project-rate-guide {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 18px;
}

.project-rate-guide div {
  padding: 14px 16px;
  border: 1px solid var(--preika-blue-200);
  border-radius: 12px;
  background: var(--preika-blue-050);
}

.project-rate-guide strong,
.project-rate-guide span {
  display: block;
}

.project-rate-guide strong {
  margin-bottom: 4px;
  color: var(--preika-blue-900);
  font-size: 11px;
}

.project-rate-guide span {
  color: var(--preika-text-muted);
  font-size: 9px;
  line-height: 1.7;
}

.project-rate-table__group-cell {
  min-width: 150px;
  background: var(--preika-gray-050);
  vertical-align: middle;
}

.project-rate-table__previous {
  color: var(--preika-text-muted);
  background: #fbfcfd;
}

.project-rate-table__previous small {
  display: block;
  margin-top: 3px;
  font-size: 7px;
}

.project-rate-table__input {
  min-width: 150px;
}

.project-rate-table__input .form-group {
  margin-bottom: 0;
}

.project-rate-table__input .error {
  display: block;
  margin-top: 4px;
  font-size: 8px;
}

.project-rate-status {
  display: inline-flex;
  padding: 5px 9px;
  border-radius: 999px;
  font-size: 8px;
  font-weight: 800;
}

.project-rate-status--saved {
  background: var(--preika-success-100);
  color: var(--preika-success-700);
}

.project-rate-status--new {
  background: var(--preika-warning-100);
  color: var(--preika-warning-700);
}

.project-rate-status--inherited {
  background: var(--preika-blue-100);
  color: var(--preika-blue-800);
}

.project-rate-status--excluded {
  background: var(--preika-gray-150);
  color: var(--preika-gray-700);
}

.project-rate-table__exclusion {
  min-width: 130px;
}

.project-rate-exclusion {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 9px;
  font-weight: 700;
}

.project-rate-exclusion input {
  width: 16px;
  height: 16px;
}

.project-rate-table__exclusion small {
  display: block;
  margin-top: 4px;
  color: var(--preika-text-muted);
  font-size: 7px;
}

.project-rate-footer {
  align-items: center;
  margin-top: 18px;
  padding: 16px;
  border: 1px solid var(--preika-border);
  border-radius: 12px;
  background: var(--preika-gray-050);
}

.project-rate-footer p {
  margin: 0;
  color: var(--preika-text-muted);
  font-size: 9px;
}

@media (max-width: 767.98px) {
  .project-rate-search,
  .project-rate-heading,
  .project-rate-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .project-rate-search__actions .btn,
  .project-rate-heading__actions .btn {
    flex: 1;
  }

  .project-rate-guide {
    grid-template-columns: 1fr;
  }
}
</style>
